import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import PublicNavigation from '../../components/ui/PublicNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { prospectService } from '../../services/prospectService';
import QuoteDetailModal from './components/QuoteDetailModal';
import QuoteFilters from './components/QuoteFilters';
import ConversionAnalytics from './components/ConversionAnalytics';
import PerformanceMetrics from './components/PerformanceMetrics';

const OpenAIQuoteHistoryManagement = () => {
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: 'all',
    shippingType: 'all',
    clientType: 'all'
  });

  useEffect(() => {
    loadQuotes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [quotes, searchTerm, filters]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const result = await prospectService?.getAllProspects();
      setQuotes(result || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
      toast?.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...quotes];

    // Search filter
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(q => 
        q?.name?.toLowerCase()?.includes(search) ||
        q?.email?.toLowerCase()?.includes(search) ||
        q?.id?.toLowerCase()?.includes(search)
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(q => q?.quoteStatus === filters?.status);
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const ranges = {
        today: 1,
        week: 7,
        month: 30,
        quarter: 90
      };
      const days = ranges?.[filters?.dateRange];
      if (days) {
        const cutoffDate = new Date(now?.getTime() - days * 24 * 60 * 60 * 1000);
        filtered = filtered?.filter(q => new Date(q?.createdAt) >= cutoffDate);
      }
    }

    // Shipping type filter
    if (filters?.shippingType !== 'all') {
      filtered = filtered?.filter(q => 
        q?.quoteItems?.[0]?.shippingType === filters?.shippingType
      );
    }

    // Client type filter
    if (filters?.clientType !== 'all') {
      filtered = filtered?.filter(q => 
        q?.quoteItems?.[0]?.clientType === filters?.clientType
      );
    }

    setFilteredQuotes(filtered);
  };

  const handleViewDetails = (quote) => {
    setSelectedQuote(quote);
    setShowModal(true);
  };

  const handleUpdateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await prospectService?.updateProspectStatus(quoteId, newStatus);
      toast?.success('Statut mis à jour');
      loadQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast?.error('Erreur lors de la mise à jour');
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Référence', 'Date', 'Client', 'Email', 'Montant', 'Statut'],
      ...filteredQuotes?.map(q => [
        q?.id?.slice(0, 8),
        format(new Date(q?.createdAt), 'dd/MM/yyyy'),
        q?.name,
        q?.email,
        parseFloat(q?.totalAmount || 0)?.toFixed(2),
        q?.quoteStatus
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window?.URL?.createObjectURL(blob);
    const a = document?.createElement('a');
    a?.setAttribute('href', url);
    a?.setAttribute('download', `devis-openai-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    a?.click();
    toast?.success('Export réussi');
  };

  const statusLabels = {
    pending: 'En attente',
    confirmed: 'Confirmé',
    expired: 'Expiré'
  };

  const statusColors = {
    pending: 'bg-orange-100 text-orange-800',
    confirmed: 'bg-green-100 text-green-800',
    expired: 'bg-gray-100 text-gray-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Chargement des devis...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      <PublicNavigation />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center space-x-2">
                <Icon name="Sparkles" size={32} className="text-green-600" />
                <span>Gestion des devis OpenAI</span>
              </h1>
              <p className="text-gray-600 mt-1">Suivi et analyse des devis générés par intelligence artificielle</p>
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={handleExport} variant="outline" className="flex items-center space-x-2">
                <Icon name="Download" size={18} />
                <span className="hidden sm:inline">Exporter</span>
              </Button>
              <Button onClick={loadQuotes} className="flex items-center space-x-2">
                <Icon name="RefreshCw" size={18} />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email ou référence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="pl-10 w-full"
            />
          </div>
        </div>

        {/* Filters */}
        <QuoteFilters filters={filters} setFilters={setFilters} />

        {/* Performance Metrics */}
        <PerformanceMetrics quotes={quotes} />

        {/* Conversion Analytics */}
        <ConversionAnalytics quotes={quotes} />

        {/* Quotes Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Devis ({filteredQuotes?.length})
            </h2>
          </div>

          {filteredQuotes?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="FileQuestion" size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun devis trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Référence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Montant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IA Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredQuotes?.map((quote) => {
                    const shippingType = quote?.quoteItems?.[0]?.shippingType || 'N/A';
                    const aiScore = quote?.quoteContent ? 95 : 0; // Mock AI confidence score

                    return (
                      <tr key={quote?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{quote?.id?.slice(0, 8)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {format(new Date(quote?.createdAt), 'dd MMM yyyy', { locale: fr })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{quote?.name}</div>
                          <div className="text-sm text-gray-500">{quote?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {shippingType?.length > 20 ? shippingType?.slice(0, 20) + '...' : shippingType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {parseFloat(quote?.totalAmount || 0)?.toFixed(2)} €
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors?.[quote?.quoteStatus] || statusColors?.pending}`}>
                            {statusLabels?.[quote?.quoteStatus] || 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <Icon name="Sparkles" size={16} className="text-green-600" />
                            <span className="text-sm font-medium text-green-600">{aiScore}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Button
                            onClick={() => handleViewDetails(quote)}
                            variant="outline"
                            size="sm"
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quote Detail Modal */}
      {showModal && selectedQuote && (
        <QuoteDetailModal
          quote={selectedQuote}
          onClose={() => setShowModal(false)}
          onUpdateStatus={handleUpdateQuoteStatus}
        />
      )}
    </div>
  );
};

export default OpenAIQuoteHistoryManagement;